import { alpha } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useSession } from '@/auth/Session';
import { useAppConstantsContext } from '@/context/AppConstants';
import { Button, Icons, Modal, Stack, Text, styled } from '@/theme/index';
import { contactUsCheckRedirection } from '@/utils/contactUsCheck/contactUsCheck';
import { useRedirect } from '@/utils/hooks/useRedirect';
import { DateUtil } from '@/utils/index';

interface CancelFinalStepModalProps {
  /** If true, the modal is open */
  isOpen: boolean;
  onClose: () => void;
}
/**
 * This component is the final step of the online cancel flow.
 * It simply informs the user that they have cancelled, shows them
 * when their membership will end, and directs them to the my account page.
 */

const SurveyMessage = styled(Stack)(({ theme }) => ({
  color: theme.palette.progress?.main || theme.palette.primary.main,
  border: `1px solid ${
    theme.palette.progress?.main || theme.palette.primary.main
  }`,
  borderRadius: theme.spacing(0.5),
  marginTop: theme.spacing(2),
  backgroundColor: alpha(
    theme.palette.progress?.main || theme.palette.primary.main,
    0.06
  ),
}));

export function CancelFinalStepModal({
  isOpen,
  onClose,
}: CancelFinalStepModalProps) {
  const {
    session: { account },
  } = useSession();

  const { redirect } = useRedirect();
  const queryClient = useQueryClient();

  const dateUtil = new DateUtil();

  const [endDate, setEndDate] = useState('');
  const [name, setName] = useState('');

  const handleClose = () => {
    onClose();
    queryClient.refetchQueries(['account']);
  };
  const {
    data: { brandName },
    links: { cancelSurveyLink },
  } = useAppConstantsContext();

  const contactUsFormCheck = () => {
    const shouldRedirect = contactUsCheckRedirection(true, brandName);

    if (shouldRedirect === false) {
      redirect('/dashboard/billing');
    }
  };

  useEffect(() => {
    setEndDate(
      dateUtil.parseDateFromString(
        account?.account?.subscription_info?.normalize_date || '',
        'MM/dd/yyyy'
      ) || ''
    );
    setName(account?.account?.user_info?.first_name || '');
  }, [account]);

  return (
    <Modal
      open={isOpen}
      title={`We will miss you ${name}!`}
      sx={{ textAlign: 'center' }}
    >
      <Text>
        We have cancelled your subscription. You will be able to access your
        membership until <span style={{ fontWeight: 'bold' }}>{endDate}</span>.
      </Text>
      {cancelSurveyLink && (
        <SurveyMessage direction="row" padding={3} spacing={2}>
          <Icons.ContentPaste />
          <Text textAlign="left">
            We would love to know why you canceled as we are always looking for
            ways to improve. If you don’t mind please take this survey.
          </Text>
        </SurveyMessage>
      )}
      <Button
        variant={cancelSurveyLink ? 'text' : 'contained'}
        color={cancelSurveyLink ? 'primary' : 'success'}
        onClick={() => {
          contactUsFormCheck();
          handleClose();
        }}
        sx={{ marginTop: 5 }}
      >
        Go to Account
      </Button>
      {cancelSurveyLink && (
        <Button
          variant="contained"
          color="success"
          onClick={() => {
            window.open(cancelSurveyLink);
          }}
          sx={{ marginTop: 5, marginLeft: 1 }}
        >
          Take Survey
        </Button>
      )}
    </Modal>
  );
}
